/**
 * GxScoreboard: value created for every bank, split into the years played and the look-through (FB-6): the same columns and figures as the Results scoreboard.
 * $/share first (OD5-2) with the points beside it ("+5.06 pts"). Without `score.components` (results stored before ADR-0047) the two parts read "n/a".
 * One short note under the table says what every figure is compared with.
 */
import type { ReactNode } from "react";
import type { MarketView } from "@bsim/contracts";
import { formatMetric, RANK_RULE } from "@gxs/metric-defs";
import { displayRank, formatPoints, formatPointsWord, formatUsdPerShare, ordinal } from "../format";
import { GxTerm } from "../theme/GxTerm";
import { GxNotes } from "../theme/GxNotes";
import { GxPill } from "../theme/GxPill";
import { GxTierChip } from "./Score";
import { GATE_RANK_REASON, printedCash, printedParts, rankLine } from "./labels";
import "./scoreboard.css";

/** The slice of a market bank the board reads; the room finale builds the same shape from its ranking. */
export type ScoreboardBank = Pick<MarketView["banks"][number], "name" | "isAi" | "rank" | "score" | "gateRankedLast"> & { readonly teamId: string; readonly seatId: string };
export type ScoreboardVariant = "cockpit" | "results" | "finale";

const usd = (v: number) => formatUsdPerShare(v, { signed: true });

/** One diverging bar per row on a shared scale: zero (your twin) at the centre, ahead of it to the right. */
function Bar({ value, peak, me, gated }: { readonly value: number; readonly peak: number; readonly me: boolean; readonly gated: boolean }) {
  const half = peak === 0 ? 0 : Math.min(Math.abs(value) / peak, 1) * 50;
  const tone = gated ? "gate" : me ? "me" : value >= 0 ? "ahead" : "behind";
  return (
    <span className="gx-sb-bar" aria-hidden="true">
      <i className="gx-sb-fill" data-tone={tone} style={{ width: `${half}%`, ...(value >= 0 ? { left: "50%" } : { right: "50%" }) }} />
    </span>
  );
}

/** Years played (share price and cash returned, rounded to add up with the look-through) and the look-through, in $ a share; "n/a" for results stored before the split. */
function Parts({ bank }: { readonly bank: ScoreboardBank }) {
  const c = bank.score?.components;
  if (bank.score === null) return <><td data-label="Years played">{usd(0)}</td><td data-label="Value of the two projected years">{usd(0)}</td></>;
  if (c === undefined) return <><td data-label="Years played">n/a</td><td data-label="Value of the two projected years">n/a</td></>;
  const parts = printedParts(c, bank.score.valueCreatedUsdPerShare);
  const cash = printedCash(c, bank.score.valueCreatedUsdPerShare);
  return (
    <>
      <td data-label="Years played" title={`Share price ${usd(parts.price)} · cash returned ${usd(cash.cash)} (dividends ${usd(cash.dividends)}, buyback effect ${usd(cash.buyback)}), per share vs your twin`}>{usd(Math.round((parts.price + parts.cash) * 100) / 100)}</td>
      <td data-label="Value of the two projected years">{usd(parts.lookThrough)}</td>
    </>
  );
}

/** The score in points as the Results scoreboard prints it: "+5.06 pts", U+2212 for a minus. */
const pointsOf = (rate: number): string => formatPoints(rate * 100, { dp: 2, signed: true, short: true });

/** The one note under the table: what every figure is compared with, and what rank and tier mean. */
const NOTE = "Every figure is $ a share against that bank's own status-quo twin (what it would have earned changing nothing), for a share held at the start, in today's money. Rank is on the official score in points; the tier is a band of a bank's own score, not a place. A rival's figure is not against your bank.";

function Row({ bank, you, peak, reason, provisional }: { readonly provisional: boolean; /** The room and the console (no bank of their own) say why a gated bank sits below; a player reads the gate's own text under the table instead. */ readonly reason: boolean; readonly bank: ScoreboardBank; readonly you: boolean; readonly peak: number }) {
  const gated = bank.gateRankedLast === true;
  const score = bank.score;
  const value = score?.valueCreatedUsdPerShare ?? 0;
  const tooltip = score === null ? undefined : `${formatPointsWord(score.valueCreatedRate, { signed: true, dp: 2 })} official${score.valueCreatedUsd === null ? "" : ` · ${formatMetric(score.valueCreatedUsd / 1e6, "usdM", { signed: true })} for all shareholders, in today's money`}`;
  const tied = (score?.tiedWithTeamIds.length ?? 0) > 0;
  const place = displayRank(bank);
  return (
    <tr className={you ? "gx-sb-me" : undefined} data-gated={gated} data-testid={`score.board.${bank.seatId}`}>
      <td className="gx-sb-rank" data-testid={`score.board.rank.${bank.seatId}`}>{bank.rank === null ? "–" : `${tied ? "Tied " : ""}${typeof place === "number" ? ordinal(place) : place}`}</td>
      <th scope="row">
        <span className="gx-sb-name">{bank.name}{you ? " · you" : ""}{bank.isAi === true ? <> <GxPill tone="info">AI</GxPill></> : null}{gated ? <> <GxPill tone="warn">Rated 3 or worse</GxPill></> : null}</span>
        <Bar value={value} peak={peak} me={you} gated={gated} />
      </th>
      <td className="gx-sb-value" title={tooltip}>
        <b>{usd(value)}</b>{score === null ? <small>On its own twin</small> : null}
      </td>
      <td className="gx-sb-points" data-testid={`score.board.pp.${bank.seatId}`}>{score === null ? "—" : pointsOf(score.valueCreatedRate)}</td>
      <Parts bank={bank} />
      <td className="gx-sb-tier" data-label="Tier">
        {score === null ? null : <GxTierChip own={you} tier={score.tier} ground="light" provisional={provisional} testId={`score.board.tier.${bank.seatId}`} />}
        {gated && reason ? <small data-testid={`score.board.gate.${bank.seatId}`}>{GATE_RANK_REASON}</small> : null}
      </td>
    </tr>
  );
}


export function GxScoreboard({ banks, youSeatId, variant, rankRule, provisional = false, ownLineOnly = false, notes }: { /** The solo finale prints only the viewer's own rank line (a rival's gap is not the player's news) and puts its own notes first, so the table has one notes block. */ readonly ownLineOnly?: boolean; readonly notes?: ReactNode; /** The tier can still change until the finale: the chip carries the clock. */ readonly provisional?: boolean; readonly banks: readonly ScoreboardBank[]; readonly youSeatId: string | null; readonly variant: ScoreboardVariant; readonly rankRule?: string | undefined }) {
  const rows = [...banks].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
  const peak = Math.max(0, ...rows.map((b) => Math.abs(b.score?.valueCreatedUsdPerShare ?? 0)));
  const values = rows.map((b) => b.score?.valueCreatedUsdPerShare ?? 0);
  const outranked = rows.some((b, i) => b.gateRankedLast === true && values.slice(0, i).some((v) => v < (values[i] ?? 0)));
  const lines = rows.flatMap((b) => { if (ownLineOnly && b.seatId !== youSeatId) return []; const text = rankLine(b, rows); return text === null ? [] : [{ teamId: b.teamId, seatId: b.seatId, text: `${b.seatId === youSeatId ? "You" : b.name} \u00b7 ${text}` }]; });
  // The gate's one explanation, as the market words it for the viewer's own bank (`score.gate.text`); a viewer with no bank here (the facilitator) is told nothing in the viewer's voice.
  const gateText = rows.find((b) => b.seatId === youSeatId)?.score?.gate?.text;
  return (
    <div className="gx-sb" data-variant={variant} data-testid={`score.board.${variant}`}>
      <div className="gx-sb-scroll">
        <table className="gx-table gx-num gx-sb-table">
          <caption className="gx-sr">Value created against each bank&apos;s own twin, split into the years played and the look-through, with each bank&apos;s rank and tier</caption>
          <thead>
            <tr>
              <th scope="col">Rank</th>
              <th scope="col">Bank</th>
              <th scope="col">Value created<small>$ a share</small></th>
              <th scope="col" className="gx-sb-sort" aria-sort="descending" title="Banks are ranked on this column.">Points <span aria-hidden="true">{"\u25bc"}</span><small>of the twin&apos;s value, ranked on this</small></th>
              <th scope="col" title="The score from the years played: share price and cash returned.">Years played<small>$ a share</small></th>
              <th scope="col"><GxTerm term="look-through">Value of the two projected years</GxTerm><small>$ a share</small></th>
              <th scope="col">Tier</th>
            </tr>
          </thead>
          <tbody>{rows.map((b) => <Row key={b.teamId} bank={b} you={b.seatId === youSeatId} peak={peak} reason={youSeatId === null} provisional={provisional} />)}</tbody>
        </table>
      </div>
      {notes === undefined || notes === null ? null : <div className="gx-sb-extra gx-small gx-faint">{notes}</div>}
      <GxNotes testId="score.board.notes" notes={[
        ...lines.map((l) => <span key={l.teamId} data-testid={`score.board.rankline.${l.seatId}`}>{l.text}</span>),
        <span key="note" data-testid="score.board.note">{NOTE}</span>,
        outranked ? <span key="rule" data-testid="score.board.rankrule">{rankRule ?? RANK_RULE}</span> : null,
        gateText === undefined ? null : <span key="gate" data-testid="score.board.gatenote">{gateText}</span>,
      ]} />
    </div>
  );
}
